export const en = {
  // Header
  nav: {
    home: "Home",
    skills: "Skills",
    experience: "Experience",
    projects: "Projects",
    contact: "Contact & Resume"
  },

  // Home
  home: {
    role: "Maintenance Operations Manager",
    bio1: "Hello and welcome to my portfolio! I am currently a multi-technical maintenance operations manager at a public-access venue (ERP) in Montpellier, where I lead a team of 3 technicians (HVAC, electrical, all-round) on a 70,000 m² tertiary site hosting over 500,000 visitors per year.",
    bio2: "With strong field experience and a passion for automation, I combine operational management with cross-functional missions (operations support, SAP data consolidation). I innovate daily to optimize processes through Industry 4.0 (PowerBI, Power Automate), circular economy (3D printing), and continuous improvement."
  },

  // Language Skills
  languageSkills: {
    french: "French",
    english: "English",
    spanish: "Spanish",
    nativeLevel: "Native",
    advancedLevel: "C1",
    intermediateLevel: "B1",
    nativeProficiency: "Native Language",
    advancedProficiency: "Advanced",
    intermediateProficiency: "Intermediate",
    reading: "Reading",
    writing: "Writing",
    speaking: "Speaking",
    listening: "Listening",
    basicWriting: "Basic Writing",
    basicSpeaking: "Basic Speaking",
    proficiency: "Proficiency"
  },

  // Tech Skills (just category titles, as there are too many skills to translate)
  techSkills: {
    maintenanceSkills: "Maintenance Skills",
    gmaoSystems: "CMMS & ERP Systems",
    performanceMonitoring: "Performance Monitoring",
    continuousImprovement: "Continuous Improvement",
    projectManagement: "Project Management",
    designEngineering: "Design & Engineering",
    officeProductivity: "Office & Productivity",
    databasesData: "Databases & Data",
    osSystems: "Operating Systems & Infrastructure",
    programmingLanguages: "Programming Languages",
    devOpsTools: "DevOps & Tools",
    webTechnologies: "Web Technologies"
  },

  projectTechnologies: {
    m5stick: "M5 Stick",
    blynk: "Blynk",
    debian: "Debian",
    raspberrypi: "Raspberry Pi",
    apiYaskawa: "Yaskawa PLC",
    vfd: "VFD",
    energyEfficiency: "Energy Efficiency",
    roiAnalysis: "ROI Analysis",
    compressor: "Compressor",
    economy: "Economy",
    design3d: "3D Design",
    printing3d: "3D Printing",
    cad: "CAD",
    formlabs: "Formlabs",
    sustainability: "Sustainability"
  },

  footer: {
    contact: "Contact Me",
    rights: "All rights reserved."
  },

  techSkillsList: {
    maintenance: {
      system: { name: "System Maintenance", details: "Preventive and corrective maintenance procedures for industrial equipment and IT infrastructure." },
      reliability: { name: "Reliability", details: "MTBF/MTTR analysis, reliability index calculation, failure review, and implementation of improvement strategies." },
      predictive: { name: "Predictive Maintenance", details: "Implementation of predictive analysis systems, RCM (Reliability Centered Maintenance) techniques, vibration monitoring, and thermography." },
      tpm: { name: "TPM", details: "Deployment of Total Productive Maintenance, operator training for autonomous maintenance, elimination of six big losses." },
      troubleshooting: { name: "Troubleshooting", details: "Systematic problem diagnosis, root cause analysis, and resolution for mechanical and software issues." },
      writing: { name: "Technical Writing", details: "Writing of procedures, operating modes, maintenance ranges, and training materials." }
    },
    gmao: {
      sap: { name: "SAP", details: "Planning, maintenance ranges, cost allocations, and work order management with SAP PM." },
      dimomaint: { name: "DIMOMAINT", details: "Use of DIMOMAINT CMMS." },
      cegid: { name: "CEGID PMI", details: "Use of CEGID for stock management, movements, and metrology." },
      aqmanager: { name: "AQMANAGER", details: "Installation, configuration, and use of AQMANAGER tool for CMMS." }
    },
    performance: {
      kpi: { name: "KPI", details: "Definition and tracking of key performance indicators adapted to maintenance and production activities." },
      dashboards: { name: "Dashboards", details: "Creation of dashboards for machine fleet monitoring, availability rates, OEE, and maintenance costs." }
    },
    continuousImprovement: {
      lean: { name: "Lean Management", details: "Application of Lean principles to reduce waste, optimize flows, and create value." },
      '5s': { name: "5S", details: "Implementation of 5S (Seiri, Seiton, Seiso, Seiketsu, Shitsuke) for workplace organization and standardization." },
      kaizen: { name: "Kaizen", details: "Facilitation of Kaizen events, implementation of continuous improvement, and deployment of progressive change culture." },
      pareto: { name: "Pareto", details: "Pareto analysis (80/20), prioritization of critical problems, focus on high-impact actions." }
    },
    projectManagement: {
      jira: { name: "Jira", details: "Project tracking, issue management, and workflow automation with Jira." },
      agile: { name: "AGILE", details: "Implementation of Agile methodologies including Scrum and Kanban for project delivery." },
      sprint: { name: "SPRINT", details: "Planning and facilitation of sprint ceremonies, including planning, review, and retrospective meetings." }
    },
    design: {
      fusion: { name: "Fusion 360", details: "3D modeling, parametric design, and simulation for mechanical components and assemblies." },
      arduino: { name: "Arduino", details: "Prototyping of IoT devices and automation systems using Arduino microcontrollers and sensors." },
      fritzing: { name: "Fritzing", details: "Electronic circuit design and PCB layout for embedded systems and interface projects." }
    },
    office: {
      o365: { name: "Office 365", details: "Advanced use of the complete Office 365 suite, including SharePoint administration, Teams, and Exchange." },
      powerAutomate: { name: "Power Automate", details: "Workflow automation with Power Automate, including connectors to various systems and custom API integrations." },
      powerApps: { name: "PowerApps", details: "Development of custom business applications with PowerApps using SQL and SharePoint data sources." },
      powerBi: { name: "Power BI", details: "Creation of interactive dashboards and reports with complex data models and DAX calculations." }
    },
    data: {
      sql: { name: "SQL", details: "Database design and optimization, complex queries, stored procedures, and data migrations." },
      dax: { name: "DAX", details: "Creation of measures and calculated columns with Data Analysis Expressions for Power BI models." },
      powerQuery: { name: "Power Query (M)", details: "ETL using M language for data transformation, cleaning, and integration from various sources." }
    },
    os: {
      debian: { name: "Debian", details: "Server administration, package management, and system optimization on Debian-based distributions." },
      bash: { name: "Bash/Shell", details: "Automation via scripts for system tasks, data processing, and scheduled maintenance operations." }
    },
    programming: {
      cpp: { name: "C++", details: "Embedded systems programming, primarily ESP8266, Raspberry, M5Stack." },
      csharp: { name: "C#", details: "Windows application development with .NET Framework and integration with SQL Server databases." },
      vba: { name: "VBA", details: "Excel application automation, custom functions, and user interfaces for business processes." },
      python: { name: "Python", details: "Data analysis, automation scripts, and API integrations with libraries like Pandas and NumPy." }
    },
    devops: {
      git: { name: "Git", details: "Version control workflows, branching strategies, and collaborative development with Git and GitHub." },
      llm: { name: "LLM", details: "Use of online LLMs (Claude Sonnet, ChatGPT, Perplexity...) and local LLM tweaking for job-related productivity." }
    },
    web: {
      node: { name: "Node.js", details: "Backend API development, middleware implementation, and server-side rendering for web applications." }
    }
  },

  // Experience
  experience: {
    title: "Professional Experience",
    present: "Present",
    years: "year",
    yearsPlural: "years",
    months: "months",
    workType: "work",
    educationType: "education",
    certificationType: "certification",
    skillsType: "skills",
    vinciGlobalPeriod: "April 2024 - Present",
    vinciGlobalLabel: "VINCI Global Period",
    // Experience entries
    experiences: {
      vinci: {
        title: "Maintenance Team Leader - CFO CFA Montpellier Region",
        company: "VINCI Facilities - Montpellier📍",
        period: "April 2024 - December 2025",
        description: "Management of a 7-person team responsible for high and low voltage maintenance (HV substations, generators, intrusion, fire detection).\n\nSteering preventive and corrective maintenance with management of planning, on-call duties, and operational priorities.\n\nManagement and monitoring of client contracts, including satisfaction and contractual commitments.\n\nCreation of quotes, negotiation, and challenging suppliers to optimize costs and service quality.\n\nImplementation of continuous improvements through automation of client reports, quote creation and sending, and equipment management using my skills in Excel, PowerApps, and Power Automate.\n\nThese optimizations saved 1.5 hours per day, improving productivity and client responsiveness.",
        skills: ["Team Management", "Preventive Maintenance", "Contract Management", "Process Automation", "SAP", "Advanced Excel", "PowerApps", "Power Automate"]
      },
      vinciErp: {
        title: "Maintenance Operations Manager - Public-Access Venue, Montpellier",
        company: "VINCI Facilities - Montpellier📍",
        period: "January 2026 - Present",
        description: "Responsible for a team of 3 multi-specialist technicians (HVAC, electrical, all-round) at a public-access venue (ERP) in Montpellier, a tertiary site of nearly 70,000 m² hosting over 500,000 visitors per year.\n\nSteering operations and multi-technical maintenance in a demanding context: regulatory constraints, on-call duties, and service continuity on a high-traffic site.\n\nDevelopment of custom internal solutions to improve production and operational efficiency.\n\n1st place individually (out of 100+ participants) at the Vinci Energies CTF (Cybersecurity, OSINT, vulnerability exploitation).",
        skills: ["Team Management", "HVAC", "Electrical", "ERP", "Cybersecurity", "On-call duties", "Multi-technical", "Power Platform"]
      },
      atole: {
        title: "Maintenance Technician",
        company: "ATOLE - Castries📍",
        period: "September 2019 - April 2024",
        description: "Maintenance of equipment necessary for the manufacture of electrical equipment (cabinets, technical ducts).\n\nPreventive and corrective maintenance on punching machines, laser cutting, semi-automatic welders, bending machines (large fleet of AMADA, TRUMPF machines).\n\nPreventive and corrective maintenance on line team equipment (pneumatic screwdrivers, staplers, riveters, gas ovens, paint workshop).\n\nDevelopment of Industry 4.0 with sensors, Arduino, PowerBI, SQL, Debian, and PCB design.\n\nWriting of more than 100 documents (operating procedures, range, training).\n\nInformal deputy maintenance manager (management, training of new arrivals, projects).",
        skills: ["Industrial Maintenance", "AMADA & TRUMPF Equipment", "Arduino", "SQL", "PCB Design", "PowerBI", "Debian", "Technical Documentation"]
      },
      grtgaz: {
        title: "Maintenance Technician",
        company: "GRTgaz - Nozay📍",
        period: "September 2016 - July 2019",
        description: "Service maintenance of 5 turbocompressors used to compress gas, as well as the surrounding gas process.\n\nWaste management (ADR removals).\n\nManagement of work in cooperation with external companies (often foreign).",
        skills: ["Turbocompressor Maintenance", "Gas Systems", "Waste Management", "Contractor Coordination", "Industrial Equipment"]
      },
      bts: {
        title: "BTS in Energy and Fluid Systems Maintenance",
        company: "Education - Aix En Provence📍",
        period: "2015 - 2016",
        description: "BTS in Energy and Fluid Systems Maintenance, deepening previous qualifications with a specialization in industrial systems.\n\nCurrently pursuing a Professional License in Maintenance through VAE.",
        skills: ["Energy Systems", "Fluid Mechanics", "Industrial Maintenance", "Technical Documentation"]
      },
      bac: {
        title: "Professional Baccalaureate in I.E.M",
        company: "Education - Istres📍",
        period: "2013 - 2015",
        description: "Professional Baccalaureate in Industrial Equipment Maintenance (I.E.M), comprehensive training on maintenance techniques and industrial systems.",
        skills: ["Equipment Maintenance", "Troubleshooting", "Technical Drawing", "Preventive Maintenance"]
      },
      certifications: {
        title: "Professional Certifications",
        company: "Various certification bodies",
        period: "2016 - Present",
        description: "Multiple professional certifications including First Aid & CPR/AED (equivalent to SST), Electrical Safety / Arc Flash Training (equivalent to GIES1), Forklift Operator Certification (equivalent to CACES 3), Swagelok Tube Fitting Installation, High/Low Voltage Authorization (equivalent to HT/BT), and Fall Protection / Working at Heights Training (equivalent to harness use).",
        skills: ["Safety Certifications", "Equipment Operation", "Electrical Safety", "First Aid", "CACES 3"]
      },
      technical: {
        title: "Technical Skills",
        company: "Applications & Languages",
        period: "Ongoing",
        description: "Mastery of CMMS and ERP software: SAP, DIMOMAINT, CEGID PMI, AQMANAGER.\n\nOffice and productivity: Office 365, PowerPlatform.\n\nDesign and prototyping: Fusion 360, Arduino, Fritzing.\n\nLanguages and systems: Debian, Bash, Shell, C++, C#, VBA, PYTHON, SQL, DAX, POWER QUERY (M).",
        skills: ["SAP", "Office 365", "PowerPlatform", "Fusion 360", "Arduino", "Python", "SQL", "VBA"]
      }
    }
  },

  // Appointment Form
  appointment: {
    title: "Maintenance Manager",
    intro: "As an experienced Maintenance Manager, I have developed solid expertise in team management, equipment optimization, and continuous improvement of industrial processes. With several years of experience, I put my skills at the service of infrastructure development and reliability, combining maintenance strategy and operational performance.",
    scheduleInterview: "Schedule an Interview",
    scheduleDesc: "Book a video call slot",
    contactLinkedIn: "Contact me on LinkedIn",
    linkedInDesc: "Check my LinkedIn profile to learn more about my professional background",
    sendEmail: "Send me an email",
    emailDesc: "Contact me by email for an initial exchange",
    downloadCV: "Download my Resume",
    cvDesc: "Get a copy of my resume to discover my skills",
    availability: "Thank you for your visit",
    availabilityStatus: ""
  },

  // Projects
  projects: {
    title: "My Projects",
    dmhCounter: {
      title: "DMH Counter",
      description: "Monitoring of the product during production plus DMH threshold with alerting to respect production times."
    },
    compressorEfficiency: {
      title: "Compressor Efficiency",
      description: "Analysis of efficiency gains from converting an air compressor to a VFD, showing a 2-year ROI on a €20k investment."
    },
    threeDPrinting: {
      title: "3D Printing Savings",
      description: "Several thousand euros in savings on several projects."
    }
  }
};
